import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Crown, 
  CreditCard, 
  Users, 
  ShieldCheck,
  ChevronRight,
  Star,
  Check,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Hotel,
  UtensilsCrossed,
  Sparkles,
  Dumbbell,
  Waves,
  Music,
  PartyPopper,
  Building2,
  Menu,
  X,
  Download,
  Smartphone,
  Gift,
  Calendar,
  Home,
  Newspaper,
  Clock,
  Bell,
  Package,
  ShoppingBag,
  Car,
  Scissors,
  Heart,
  Wrench,
  Shirt,
  Droplets,
  Truck,
  Lock
} from 'lucide-react';
import { API, useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { waLink } from '@/config/contact';
import AdsCarousel from '../components/AdsCarousel';
import { DiscoverVideoSection } from '../components/DiscoverVideoSection';
import { TestimonialsSection, GallerySection, StatsBand } from '../components/PremiumSections';
import LatestNewsStrip from '../components/LatestNewsStrip';
import PremiumHero from '../components/PremiumHero';
import PremiumAbout from '../components/PremiumAbout';
import VisionMission from '../components/VisionMission';
import PremiumGallery from '../components/PremiumGallery';
import AmbientControl from '../components/AmbientControl';
import BciLogo from '../components/BciLogo';
import HomeStories from '../components/HomeStories';

// Icon mapping for dynamic content
const iconMap = {
  Hotel, UtensilsCrossed, Sparkles, Dumbbell, Waves, Music, PartyPopper, Building2
};


// Simple Plan Selector Component
const PlanSelector = ({ plans }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-[#0F0F10] rounded-2xl p-6 border border-[#D4AF37]/20"
    >
      {/* Plan Dropdown - Names Only */}
      <div className="mb-4">
        <select 
          className="w-full px-4 py-3 bg-[#1A1A1C] border border-white/10 rounded-xl text-white text-lg font-semibold focus:border-[#D4AF37] focus:outline-none cursor-pointer appearance-none"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23D4AF37'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
          value={selectedPlan?.id || ''}
          onChange={(e) => {
            const plan = plans.find(p => (p.id || p.plan_id) === e.target.value);
            setSelectedPlan(plan);
          }}
        >
          <option value="" disabled>-- Select Membership Plan --</option>
          {plans.map((plan) => (
            <option key={plan.id || plan.plan_id} value={plan.id || plan.plan_id}>
              {plan.name}
            </option>
          ))}
        </select>
      </div>

      {/* Plan Details - Shown after selection */}
      {selectedPlan ? (
        <div className="border-t border-white/10 pt-4 mt-2">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              {selectedPlan.name}
            </h3>
            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl font-bold text-[#D4AF37]">₹{selectedPlan.price?.toLocaleString()}</span>
              <span className="text-gray-400">/ {selectedPlan.duration_months} months</span>
            </div>
          </div>
          
          {selectedPlan.features && selectedPlan.features.length > 0 && (
            <ul className="space-y-2 mb-6">
              {selectedPlan.features.slice(0, 5).map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
          
          <Link 
            to={`/register?plan=${selectedPlan.id || selectedPlan.plan_id}`}
            className="block w-full py-3 bg-[#D4AF37] text-black text-center font-semibold rounded-xl hover:bg-[#E6D699] transition-all"
          >
            Join Now
          </Link>
        </div>
      ) : (
        <p className="text-center text-gray-500 text-sm py-4">
          Select a plan above to see details
        </p>
      )}
    </motion.div>
  );
};

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [partners, setPartners] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [promotionEvents, setPromotionEvents] = useState([]);
  const [promotionAds, setPromotionAds] = useState([]);
  const [promotionVideos, setPromotionVideos] = useState([]);
  const [offers, setOffers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [bitzNews, setBitzNews] = useState([]);
  const [dailyNeedsCategories, setDailyNeedsCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [aboutData, setAboutData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [storiesSignal, setStoriesSignal] = useState(0);
  const [lightboxItem, setLightboxItem] = useState(null);

  const dismissOfferPopup = () => {
    setShowOfferPopup(false);
    try { localStorage.setItem('offerPopupDismissedAt', Date.now().toString()); } catch (_) {}
  };

  useEffect(() => {
    fetchPlans();
    fetchPartners();
    fetchExperiences();
    fetchPromotions();
    fetchOffers();
    fetchGallery();
    fetchBitzNews();
    fetchDailyNeedsCategories();
    fetchSettings();
    fetchAboutData();
    
    // PWA Install prompt handler
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Show offer popup after 5 seconds (give Stories overlay first dibs)
    // Suppress if user is currently viewing Stories or just dismissed them
    const popupShown = sessionStorage.getItem('offerPopupShown');
    const dismissedAt = parseInt(localStorage.getItem('offerPopupDismissedAt') || '0', 10);
    const daysSinceDismiss = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    if (!popupShown && daysSinceDismiss > 7) {
      const timer = setTimeout(() => {
        // Skip if Stories overlay is still open
        if (document.querySelector('[data-testid="home-stories-overlay"]')) return;
        setShowOfferPopup(true);
        sessionStorage.setItem('offerPopupShown', 'true');
      }, 9000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Background slideshow auto-rotate
  useEffect(() => {
    if (partners.length > 0) {
      const interval = setInterval(() => {
        setActiveGalleryIndex(prev => (prev + 1) % Math.min(partners.length, 5));
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [partners.length]);

  const handleInstallApp = async () => {
    if (!deferredPrompt) {
      // Show instructions for manual installation
      alert('To install the app:\n\n• On Android: Tap the menu (⋮) and select "Add to Home Screen"\n• On iOS: Tap Share and select "Add to Home Screen"\n• On Desktop: Click the install icon in the address bar');
      return;
    }
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('App installed');
    }
    setDeferredPrompt(null);
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans?is_active=true&available_online=true`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners?is_active=true`);
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  const fetchExperiences = async () => {
    try {
      const response = await axios.get(`${API}/content/experiences`);
      const activeExperiences = response.data.filter(exp => exp.is_active);
      setExperiences(activeExperiences);
    } catch (error) {
      console.error('Failed to fetch experiences:', error);
      // Fallback to default experiences
      setExperiences([
        { id: '1', title: 'Luxury Hotels', image_url: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80', discount: 'Up to 40% Off', description: 'Exclusive rates at 5-star properties', icon: 'Hotel' },
        { id: '2', title: 'Fine Dining', image_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80', discount: 'Up to 25% Off', description: 'Premium restaurants worldwide', icon: 'UtensilsCrossed' },
        { id: '3', title: 'Spa & Wellness', image_url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80', discount: 'Up to 35% Off', description: 'Rejuvenate at luxury spas', icon: 'Sparkles' },
        { id: '4', title: 'Premium Gyms', image_url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80', discount: 'Up to 30% Off', description: 'Access elite fitness centers', icon: 'Dumbbell' }
      ]);
    }
  };

  const fetchPromotions = async () => {
    try {
      const [eventsRes, adminEventsRes, adsRes, videosRes] = await Promise.all([
        axios.get(`${API}/promotions/events`),
        axios.get(`${API}/events?is_active=true`).catch(() => ({ data: [] })),
        axios.get(`${API}/promotions/ads`),
        axios.get(`${API}/promotions/videos`)
      ]);
      // Merge admin "Events Management" entries with legacy promotion_events.
      // Normalize so both shapes render via the same card template.
      const adminEvents = (adminEventsRes.data || []).map(e => ({
        id: e.id,
        title: e.title || e.name,
        description: e.description,
        image_url: e.image_url || e.poster_url || e.cover_image_url,
        video_url: e.video_url,
        event_date: e.event_date || e.start_date,
        link_url: e.link_url,
        is_active: e.is_active !== false,
      }));
      const promo = (eventsRes.data || []).filter(e => e.is_active);
      // Deduplicate by id so an event isn't shown twice
      const seen = new Set(promo.map(e => e.id));
      const merged = [...promo, ...adminEvents.filter(e => e.is_active && !seen.has(e.id))];
      setPromotionEvents(merged);
      setPromotionAds((adsRes.data || []).filter(a => a.is_active));
      setPromotionVideos((videosRes.data || []).filter(v => v.is_active));
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return url;
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/content/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchAboutData = async () => {
    try {
      const response = await axios.get(`${API}/content/about-us`);
      setAboutData(response.data);
    } catch (error) {
      console.error('Failed to fetch about data:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers?is_active=true`);
      setOffers(response.data);
      // Set the first offer for popup
      if (response.data.length > 0) {
        setCurrentOffer(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/content/gallery`);
      setGallery(response.data.filter(img => img.is_active));
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    }
  };

  const fetchBitzNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setBitzNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    }
  };

  const fetchDailyNeedsCategories = async () => {
    try {
      const response = await axios.get(`${API}/daily-needs/categories`);
      setDailyNeedsCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch daily needs categories:', error);
    }
  };

  // Icon mapping for daily needs
  const dailyNeedsIconMap = {
    'package': Package,
    'shopping-bag': ShoppingBag,
    'car': Car,
    'scissors': Scissors,
    'heart': Heart,
    'home': Home,
    'utensils': UtensilsCrossed,
    'wrench': Wrench,
    'shirt': Shirt,
    'droplets': Droplets,
    'truck': Truck,
    'sparkles': Sparkles,
  };

  const getDailyNeedsIcon = (iconName) => dailyNeedsIconMap[iconName] || Package;

  const features = [
    { icon: Crown, title: 'Premium Benefits', description: 'Exclusive access to luxury partners and facilities' },
    { icon: CreditCard, title: 'Digital Membership', description: 'QR-enabled membership card accessible anywhere' },
    { icon: Users, title: 'Partner Network', description: 'Discounts at our curated partner locations' },
    { icon: ShieldCheck, title: 'Verified Access', description: 'Instant verification at all partner venues' }
  ];

  // Auto-rotate gallery
  useEffect(() => {
    if (experiences.length === 0) return;
    const interval = setInterval(() => {
      setActiveGalleryIndex((prev) => (prev + 1) % experiences.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [experiences.length]);

  // Helper to get icon component
  const getIcon = (iconName) => iconMap[iconName] || Hotel;

  return (
    <div className="min-h-screen bg-[#0F0F10]">
      {/* Facebook/Instagram Stories-style overlay — auto-opens on first visit per session */}
      <HomeStories openSignal={storiesSignal} />

      {/* Ambient sound — discrete floating toggle (bottom-left) */}
      <AmbientControl />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        {/* Blue Navigation Bar - Full Width Horizontal */}
        <div className="bg-[#1a365d] hidden md:block w-full">
          <div className="flex items-center justify-center py-0">
            <Link to="/" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center border-r border-white/20">
              <Home className="w-4 h-4" />
            </Link>
            <Link to="/about" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">ABOUT US</Link>
            <Link to="/membership" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">MEMBERSHIP</Link>
            <Link to="/offers" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">OFFERS</Link>
            <Link to="/daily-needs" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">DAILY NEEDS</Link>
            <Link to="/property-connect" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20" data-testid="nav-property-connect">PROPERTY CONNECT</Link>
            <Link to="/partner-apply" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">AFFILIATIONS</Link>
            <Link to="/gallery" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">GALLERY</Link>
            <a href="#promotions" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">EVENTS</a>
            <Link to="/news" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium border-r border-white/20">BITZ NEWS</Link>
            <Link to="/contact" className="text-white px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors text-sm font-medium">CONTACT</Link>
          </div>
        </div>
        
        {/* Yellow Accent Stripe */}
        <div className="bg-[#D4AF37] h-1.5 hidden md:block"></div>
        
        {/* White Strip - Logo (left), Contact (center), Login/Register (right) */}
        <div className="bg-white py-1 px-4 flex items-center justify-between">
          {/* Logo - Left - Bigger */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <img 
              src="/brand/bci-logo-512.png" 
              alt="BITZ Club International" 
              className="h-14 sm:h-16 w-auto object-contain"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-base font-bold text-[#1a365d] tracking-wide" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>
                BITZ Club International
              </span>
              <span className="text-[9px] text-[#D4AF37] tracking-[0.3em] uppercase font-semibold">
                — BCI · Premium Worldwide —
              </span>
            </div>
          </Link>
          
          {/* Contact - Center (Desktop only) */}
          <div className="hidden md:flex items-center gap-1 text-gray-700">
            <Phone className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-xs">For special Offers and Corporate Deals Please</span>
            <span className="text-xs font-semibold text-[#1a365d]">call +91-7812901116/ 7812901118</span>
          </div>
          
          {/* Login/Register - Right */}
          <div className="flex items-center gap-2">
            <Link 
              to="/member/login" 
              className="text-[#1a365d] hover:text-[#D4AF37] transition-colors text-xs font-semibold"
              data-testid="login-link"
            >
              Member Login
            </Link>
            <span className="text-gray-400 text-xs">|</span>
            <Link 
              to="/register" 
              className="text-[#1a365d] hover:text-[#D4AF37] transition-colors text-xs font-medium"
              data-testid="register-link"
            >
              Register
            </Link>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden text-[#1a365d] p-1 ml-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#1a365d] border-t border-white/10 px-3 py-2"
          >
            <div className="flex flex-col gap-0">
              <Link to="/about" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>ABOUT US</Link>
              <Link to="/membership" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>MEMBERSHIP</Link>
              <Link to="/offers" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>OFFERS</Link>
              <Link to="/daily-needs" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>DAILY NEEDS</Link>
              <Link to="/property-connect" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="nav-property-connect-mobile">PROPERTY CONNECT</Link>
              <Link to="/partner-apply" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>AFFILIATIONS</Link>
              <Link to="/gallery" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>GALLERY</Link>
              <a href="#promotions" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>EVENTS</a>
              <Link to="/news" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>BITZ NEWS</Link>
              <Link to="/contact" className="text-white hover:text-[#D4AF37] transition-colors py-1.5 text-xs font-medium" onClick={() => setMobileMenuOpen(false)}>CONTACT</Link>
              <div className="border-t border-white/20 pt-1.5 mt-1.5 text-white text-xs">
                <Phone className="w-3 h-3 inline mr-1" />
                Call: +91-7812901116 / 7812901118
              </div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section — Premium cinematic */}
      <PremiumHero />

      {/* Premium Stats Band */}
      <StatsBand />

      {/* Discover BITZ CLUB — promotional presentation */}
      <DiscoverVideoSection />

      {/* Partner Ads Carousel */}
      <section className="py-8 sm:py-12 bg-[#0F0F10]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <AdsCarousel placement="homepage" autoPlay={true} interval={5000} />
        </div>
      </section>

      {/* Events, Ads & Videos Section */}
      <section id="promotions" className="py-16 sm:py-24 bg-[#0F0F10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Only show if there's content */}
          {(promotionEvents.length > 0 || promotionAds.length > 0 || promotionVideos.length > 0) && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-10 sm:mb-16"
              >
                <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-widest mb-3 block">What's Happening</span>
                <h2 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Events & <span className="text-[#D4AF37]">Updates</span>
                </h2>
                <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base px-4">
                  Stay updated with our latest events, offers, and member activities
                </p>
              </motion.div>

              {/* === EVENTS GRID — full posters, click to enlarge === */}
              {promotionEvents.length > 0 && (
                <div className="mb-12">
                  <div className="flex items-center gap-2 mb-5">
                    <Calendar className="w-5 h-5 text-[#D4AF37]" />
                    <h3 className="text-white text-lg sm:text-xl font-semibold tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Upcoming Events
                    </h3>
                    <span className="ml-auto text-gray-500 text-xs uppercase tracking-widest">
                      {promotionEvents.length} {promotionEvents.length === 1 ? 'event' : 'events'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {promotionEvents.slice(0, 6).map((ev, idx) => (
                      <motion.button
                        key={ev.id || idx}
                        onClick={() => setLightboxItem({ type: 'event', data: ev })}
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.05 }}
                        className="text-left group relative bg-gradient-to-br from-[#161618] to-[#0F0F10] rounded-2xl overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-all shadow-lg shadow-black/30 hover:shadow-[#D4AF37]/10"
                        data-testid={`event-card-${idx}`}
                      >
                        {/* Full poster — preserve aspect ratio (no crop) */}
                        <div className="relative bg-black flex items-center justify-center" style={{ minHeight: 220, maxHeight: 480 }}>
                          {ev.image_url ? (
                            <img
                              src={ev.image_url}
                              alt={ev.title}
                              className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full aspect-[4/5] bg-gradient-to-br from-[#1a365d]/40 to-[#D4AF37]/20 flex items-center justify-center">
                              <Calendar className="w-16 h-16 text-[#D4AF37]/60" />
                            </div>
                          )}
                          {/* Video play badge if event has a video */}
                          {ev.video_url && (
                            <div className="absolute top-3 right-3 w-12 h-12 bg-black/70 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20">
                              <div className="w-0 h-0 border-l-[14px] border-l-white border-y-[8px] border-y-transparent ml-1" />
                            </div>
                          )}
                          <span className="absolute top-3 left-3 bg-[#D4AF37] text-black text-[10px] font-bold px-2 py-1 rounded-full tracking-widest uppercase">
                            Event
                          </span>
                        </div>
                        <div className="p-4 space-y-1">
                          <h4 className="text-white font-semibold text-base group-hover:text-[#D4AF37] transition-colors leading-tight line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                            {ev.title}
                          </h4>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                            {ev.event_date && (
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-[#D4AF37]" />
                                {new Date(ev.event_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {ev.event_time && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3 text-[#D4AF37]" />
                                {ev.event_time}
                              </span>
                            )}
                            {ev.venue && (
                              <span className="inline-flex items-center gap-1 truncate">
                                <MapPin className="w-3 h-3 text-[#D4AF37]" />
                                {ev.venue}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-[#D4AF37]/70 mt-1 group-hover:text-[#D4AF37] transition-colors">Tap to enlarge / play video →</p>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* === ADS / VIDEOS GRID (only if data exists) === */}
              {(promotionAds.length > 0 || promotionVideos.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {/* Ads */}
                  {promotionAds.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl overflow-hidden border border-white/10"
                    >
                      <div className="relative bg-black flex items-center justify-center" style={{ minHeight: 220 }}>
                        <img
                          src={promotionAds[0]?.image_url || 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800'}
                          alt={promotionAds[0]?.title || 'Promotions'}
                          className="w-full h-auto object-contain"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 bg-[#1a365d] text-white text-[10px] font-bold px-2 py-1 rounded-full tracking-widest uppercase">Offer</span>
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {promotionAds[0]?.title || 'Special Offers'}
                        </h3>
                        {promotionAds[0]?.description && (
                          <p className="text-gray-400 text-sm line-clamp-2 mt-1">{promotionAds[0].description}</p>
                        )}
                        {promotionAds[0]?.link_url && (
                          <a href={promotionAds[0].link_url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm hover:underline mt-2 inline-block">
                            Read More →
                          </a>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* Videos */}
                  {promotionVideos.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl overflow-hidden border border-white/10"
                    >
                      <div className="relative aspect-video overflow-hidden bg-black">
                        {promotionVideos[0]?.youtube_url ? (
                          <iframe
                            src={getYouTubeEmbedUrl(promotionVideos[0].youtube_url)}
                            title={promotionVideos[0].title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : promotionVideos[0]?.video_url ? (
                          <video src={promotionVideos[0].video_url} controls className="w-full h-full" preload="metadata" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                              <div className="w-0 h-0 border-l-[24px] border-l-white border-y-[14px] border-y-transparent ml-1" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-full tracking-widest uppercase">Video</span>
                        <h3 className="text-white font-bold text-lg mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {promotionVideos[0]?.title || 'Watch Now'}
                        </h3>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox for events — full-size poster + video playback */}
      {lightboxItem && lightboxItem.type === 'event' && (
        <div
          className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightboxItem(null)}
          data-testid="event-lightbox"
        >
          <button
            onClick={() => setLightboxItem(null)}
            className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="max-w-4xl w-full max-h-full overflow-y-auto bg-[#0F0F10] rounded-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxItem.data.video_url ? (
              <video src={lightboxItem.data.video_url} controls autoPlay className="w-full max-h-[60vh] bg-black" />
            ) : lightboxItem.data.image_url ? (
              <img src={lightboxItem.data.image_url} alt={lightboxItem.data.title} className="w-full max-h-[70vh] object-contain bg-black" />
            ) : null}
            <div className="p-5 space-y-2">
              <h3 className="text-white text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                {lightboxItem.data.title}
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-300">
                {lightboxItem.data.event_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-[#D4AF37]" />
                    {new Date(lightboxItem.data.event_date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                  </span>
                )}
                {lightboxItem.data.event_time && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#D4AF37]" />
                    {lightboxItem.data.event_time}
                  </span>
                )}
                {lightboxItem.data.venue && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                    {lightboxItem.data.venue}{lightboxItem.data.city ? `, ${lightboxItem.data.city}` : ''}
                  </span>
                )}
              </div>
              {lightboxItem.data.description && (
                <p className="text-gray-300 text-sm leading-relaxed pt-2">{lightboxItem.data.description}</p>
              )}
              {/* WhatsApp RSVP / Enquire */}
              <a
                href={waLink(`Hi, I'd like to know more about the event "${lightboxItem.data.title}"${lightboxItem.data.event_date ? ` on ${lightboxItem.data.event_date}` : ''}.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full text-sm font-semibold transition-colors"
                data-testid="event-rsvp-whatsapp"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
                </svg>
                Enquire / RSVP on WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Premium Editorial About */}
      <PremiumAbout />

      {/* Vision · Mission · Promise */}
      <VisionMission />


      {/* Plans Section - Simple Dropdown */}
      <section id="plans" className="py-12 sm:py-16 bg-[#1A1A1C]">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <h2 
              className="text-2xl sm:text-3xl font-bold text-white mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Choose Your <span className="text-[#D4AF37]">Membership</span>
            </h2>
          </motion.div>

          <PlanSelector plans={plans.filter(p => p.available_online !== false)} />
        </div>
      </section>

      {/* Offers Section */}
      <section id="offers" className="py-16 sm:py-24 bg-[#1A1A1C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-widest mb-3 block">Special Deals</span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Exclusive <span className="text-[#D4AF37]">Offers</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Take advantage of special deals and discounts at our partner venues
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {offers.map((offer, index) => (
              <motion.div
                key={offer.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-dark card-interactive rounded-xl sm:rounded-2xl overflow-hidden"
              >
                {offer.image_url && (
                  <div 
                    className="h-40 sm:h-48 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${offer.image_url})` }}
                  >
                    {offer.discount_text && (
                      <div className="absolute top-3 right-3 bg-[#D4AF37] text-black px-3 py-1 rounded-full text-sm font-bold">
                        {offer.discount_text}
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">{offer.title}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{offer.description}</p>
                  {offer.category && (
                    <span className="inline-block bg-white/10 text-[#D4AF37] px-3 py-1 rounded text-xs">
                      {offer.category}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Needs Section - Door-to-Door Services Preview */}
      <section id="daily-needs" className="py-16 sm:py-24 bg-[#0F0F10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-widest mb-3 block">Member Services</span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Daily <span className="text-[#D4AF37]">Needs</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              Door-to-door services exclusively for BITZ Club members. Order via WhatsApp with special discounts.
            </p>
          </motion.div>

          {dailyNeedsCategories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 mb-8">
              {dailyNeedsCategories.slice(0, 6).map((cat, index) => {
                const IconComp = getDailyNeedsIcon(cat.icon);
                return (
                  <Link
                    key={cat.id}
                    to={`/daily-needs?category=${cat.id}`}
                    className="block"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-[#1A1A1C] rounded-xl p-5 text-center hover:bg-[#252527] hover:scale-105 transition-all cursor-pointer relative group"
                    >
                      <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <IconComp className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-white font-medium text-sm">{cat.name}</h3>
                      {cat.description && (
                        <p className="text-gray-500 text-xs mt-1 line-clamp-2">{cat.description}</p>
                      )}
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 mb-8">
              {[
                { name: 'Laundry', icon: Shirt, desc: 'Wash & Iron' },
                { name: 'Car Wash', icon: Car, desc: 'Doorstep Service' },
                { name: 'Home Nurse', icon: Heart, desc: 'Healthcare' },
                { name: 'Spa at Home', icon: Sparkles, desc: 'Wellness' },
                { name: 'Home Repairs', icon: Wrench, desc: 'Maintenance' },
                { name: 'Provisions', icon: ShoppingBag, desc: 'Groceries' },
              ].map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#1A1A1C] rounded-xl p-5 text-center hover:bg-[#252527] transition-colors relative group"
                >
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Lock className="w-3 h-3 text-[#D4AF37]" />
                  </div>
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white font-medium text-sm">{item.name}</h3>
                  <p className="text-gray-500 text-xs mt-1">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          )}

          {/* Benefits Strip */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-8">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Truck className="w-4 h-4 text-[#D4AF37]" />
              <span>Free Delivery</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Star className="w-4 h-4 text-[#D4AF37]" />
              <span>Verified Vendors</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Gift className="w-4 h-4 text-[#D4AF37]" />
              <span>Member Discounts</span>
            </div>
          </div>

          {/* CTA - Simplified */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              {/* Browse Daily Needs */}
              <Link 
                to="/daily-needs" 
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#E6D699] transition-all text-sm sm:text-base"
              >
                <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                Browse Services
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
              {/* Become a Vendor */}
              <Link 
                to="/vendor-apply" 
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 border border-white/20 transition-all text-sm sm:text-base"
              >
                <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                Become a Vendor
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliations Section - Simplified with Slideshow Background */}
      <section id="affiliations" className="py-20 sm:py-32 relative overflow-hidden">
        {/* Background Slideshow of Property Photos */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 flex transition-transform duration-1000 ease-in-out" 
            style={{ 
              animation: 'slideshow 20s infinite',
              width: `${Math.max(partners.length, 4) * 100}%`
            }}
          >
            {(partners.length > 0 ? partners.slice(0, 8) : [
              { id: 1, image_url: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80' },
              { id: 2, image_url: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200&q=80' },
              { id: 3, image_url: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=80' },
              { id: 4, image_url: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80' },
            ]).map((partner, index) => (
              <div 
                key={partner.id || index}
                className="flex-shrink-0 w-full h-full bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${partner.image_url || partner.photos?.[0] || partner.logo_url || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80'})`,
                  width: `${100 / Math.max(partners.length, 4)}%`
                }}
              />
            ))}
          </div>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/80" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-widest mb-3 block">Our Network</span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Premium <span className="text-[#D4AF37]">Affiliations</span>
            </h2>
            <p className="text-gray-300 text-base sm:text-lg max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed">
              Your membership with BITZ Club opens doors to an exclusive network of Clubs, Resorts, Hotels, 
              Restaurants, Spas, and Gyms across India and abroad. Enjoy special privileges and discounts 
              at our carefully curated partner venues.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto mb-8 sm:mb-12">
              <div className="text-center">
                <p className="text-3xl sm:text-5xl font-bold text-[#D4AF37]">
                  {partners.length > 0 ? `${partners.length}+` : '400+'}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm mt-1">Partner Venues</p>
              </div>
              <div className="text-center">
                <p className="text-3xl sm:text-5xl font-bold text-[#D4AF37]">50+</p>
                <p className="text-gray-400 text-xs sm:text-sm mt-1">Cities</p>
              </div>
              <div className="text-center">
                <p className="text-3xl sm:text-5xl font-bold text-[#D4AF37]">40%</p>
                <p className="text-gray-400 text-xs sm:text-sm mt-1">Avg. Savings</p>
              </div>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
              {['Hotels & Resorts', 'Restaurants', 'Spas & Wellness', 'Gyms & Fitness', 'Clubs', 'Adventure Sports'].map((cat) => (
                <span 
                  key={cat}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs sm:text-sm"
                >
                  {cat}
                </span>
              ))}
            </div>

            {/* CTA Button */}
            <Link 
              to="/affiliations" 
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#E6D699] transition-all text-sm sm:text-base"
            >
              <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
              Explore All Affiliations
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </motion.div>
        </div>

        {/* CSS Animation for slideshow */}
        <style>{`
          @keyframes slideshow {
            0%, 20% { transform: translateX(0); }
            25%, 45% { transform: translateX(-25%); }
            50%, 70% { transform: translateX(-50%); }
            75%, 95% { transform: translateX(-75%); }
            100% { transform: translateX(0); }
          }
        `}</style>
      </section>

      {/* Premium Gallery — masonry + lightbox + filters */}
      <PremiumGallery />

      {/* Latest from BCI — compact news strip */}
      <LatestNewsStrip />


      {/* CTA Section */}
      <section className="py-16 sm:py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920)',
            filter: 'brightness(0.2)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F10] via-transparent to-[#0F0F10]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <img src="/brand/bci-logo-512.png" alt="BCI" className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 object-contain drop-shadow-[0_8px_24px_rgba(212,175,55,0.45)]" />
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6"
              style={{ fontFamily: 'Cinzel, Playfair Display, serif', letterSpacing: '0.05em' }}
            >
              Welcome to <span className="text-[#D4AF37]">BITZ Club International</span>
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
              Join thousands of members already enjoying premium worldwide privileges at partner venues across the globe.
            </p>
            <Link 
              to="/register" 
              className="btn-primary inline-flex items-center justify-center gap-2 text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-[#0F0F10] to-[#1A1A1C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-dark border border-[#D4AF37]/30 p-6 sm:p-10"
          >
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              {/* Phone Mockup */}
              <div className="order-2 lg:order-1 flex-shrink-0">
                <div className="relative">
                  <div className="w-52 h-[420px] bg-black rounded-[2.5rem] border-4 border-gray-800 overflow-hidden shadow-2xl shadow-[#D4AF37]/10">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-xl z-10" />
                    <div className="h-full bg-gradient-to-b from-[#1A1A1C] to-[#0F0F10] p-4 pt-8">
                      <div className="flex items-center gap-2 mb-6">
                        <BciLogo size={26} />
                        <div className="flex flex-col leading-tight">
                          <span className="text-white font-bold text-sm">BITZ Club</span>
                          <span className="text-[#D4AF37]/80 text-[8px] tracking-[0.2em] uppercase" style={{ fontFamily: 'Cinzel, serif' }}>International</span>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 rounded-xl p-4 mb-4 border border-[#D4AF37]/20">
                        <p className="text-[#D4AF37] text-xs font-semibold mb-1">MEMBER ID</p>
                        <p className="text-white font-bold">2607600015</p>
                        <p className="text-gray-400 text-xs mt-1">Gold Member</p>
                      </div>
                      <div className="space-y-3">
                        {['Digital Card', 'Exclusive Offers', 'Partner Deals', 'Quick Support'].map((item, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-3 flex items-center gap-3 border border-white/5">
                            <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                              <Smartphone className="w-4 h-4 text-[#D4AF37]" />
                            </div>
                            <span className="text-white text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Glow effect */}
                  <div className="absolute -inset-4 bg-[#D4AF37]/5 rounded-[3rem] -z-10 blur-xl" />
                </div>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2 flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 rounded-full text-[#D4AF37] text-sm font-medium mb-6">
                  <Download className="w-4 h-4" />
                  Available Now
                </div>
                <h2 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Download the <span className="text-[#D4AF37]">BITZ App</span>
                </h2>
                <p className="text-gray-400 mb-8 max-w-lg mx-auto lg:mx-0">
                  Access your membership benefits, digital card, exclusive offers, and partner discounts - all from your phone.
                </p>
                
                <ul className="grid sm:grid-cols-2 gap-3 mb-8 text-left max-w-lg mx-auto lg:mx-0">
                  {[
                    'Digital Membership Card',
                    'Exclusive Member Offers',
                    'Partner Discount Access',
                    'Event Notifications',
                    'Easy Referral Sharing',
                    '24/7 Support Access'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                  <button
                    onClick={handleInstallApp}
                    className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] hover:bg-[#E6D699] text-black font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="install-app-btn"
                  >
                    <Download className="w-5 h-5" />
                    Install App Now
                  </button>
                  <p className="text-gray-500 text-sm">
                    No app store needed • Works on all devices
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 bg-[#1A1A1C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 
                className="text-3xl sm:text-4xl font-bold text-white mb-4 sm:mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Get in <span className="text-[#D4AF37]">Touch</span>
              </h2>
              <p className="text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
                Have questions about our membership? Our team is here to help you discover the perfect plan for your lifestyle.
              </p>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Call Us</p>
                    <p className="text-white text-sm sm:text-base">{settings.contact_phone || '+91 78129 01118'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Email Us</p>
                    <p className="text-white text-sm sm:text-base">{settings.contact_email || 'info@thebitsclub.com'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Visit Us</p>
                    <p className="text-white text-sm sm:text-base">{settings.contact_address || 'Bangalore, Karnataka, India'}</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-xl sm:rounded-2xl overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800"
                alt="Luxury Hotel"
                className="w-full h-64 sm:h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6 sm:p-8">
                <div>
                  <p className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-wider mb-2">Premium Experience</p>
                  <p className="text-white text-lg sm:text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Join the elite community today
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Premium Privileges Gallery */}
      <GallerySection />

      {/* Latest News (Travel · Lifestyle · BCI) */}
      <LatestNewsStrip />

      {/* Member Testimonials */}
      <TestimonialsSection />

      {/* Footer */}
      <footer className="py-8 sm:py-12 bg-[#0F0F10] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/brand/bci-logo-128.png" alt="BITZ Club International" className="w-10 h-10 object-contain" />
              <div className="leading-tight">
                <span className="block text-base font-bold text-white" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>
                  BITZ Club International
                </span>
                <span className="block text-[9px] text-[#D4AF37] tracking-[0.3em] uppercase">— BCI · Premium Worldwide —</span>
              </div>
            </div>
            <p className="text-gray-500 text-sm text-center sm:text-left">
              © 2026 BITZ Club International. All rights reserved.
            </p>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link to="/member/login" className="text-gray-400 hover:text-[#D4AF37] text-sm transition-colors">Member Login</Link>
              <Link to="/membership" className="text-gray-400 hover:text-[#D4AF37] text-sm transition-colors">Membership</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#D4AF37] text-sm transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Offer Popup Modal */}
      {showOfferPopup && currentOffer && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={dismissOfferPopup}
          data-testid="offer-popup-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-gradient-to-br from-[#1A1A1C] to-[#0F0F10] border border-[#D4AF37]/30 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl my-auto"
          >
            {/* Offer Header */}
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#E6D699] p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Gift className="w-6 h-6 text-black" />
                <span className="text-black font-bold text-lg">SPECIAL OFFER!</span>
              </div>
              <p className="text-black/70 text-sm">Limited Time Only</p>
            </div>
            
            {/* Offer Content */}
            <div className="p-6 text-center">
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {currentOffer.title}
              </h3>
              <p className="text-gray-400 mb-4">{currentOffer.description}</p>
              
              {currentOffer.discount_value && (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-4">
                  <p className="text-[#D4AF37] text-3xl font-bold">
                    {currentOffer.discount_type === 'percentage' 
                      ? `${currentOffer.discount_value}% OFF`
                      : `₹${currentOffer.discount_value} OFF`}
                  </p>
                  {currentOffer.code && (
                    <p className="text-gray-400 text-sm mt-2">
                      Use Code: <span className="text-white font-mono bg-black/30 px-2 py-1 rounded">{currentOffer.code}</span>
                    </p>
                  )}
                </div>
              )}
              
              {currentOffer.valid_until && (
                <p className="text-gray-500 text-xs mb-4">
                  Valid until: {new Date(currentOffer.valid_until).toLocaleDateString()}
                </p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={dismissOfferPopup}
                  className="flex-1 px-4 py-3 border border-gray-600 text-gray-400 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Maybe Later
                </button>
                <Link
                  to="/register"
                  onClick={dismissOfferPopup}
                  className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-bold rounded-lg hover:bg-[#E6D699] transition-colors text-center"
                >
                  Join Now
                </Link>
              </div>
            </div>
            
            {/* Close Button */}
            <button
              onClick={dismissOfferPopup}
              data-testid="offer-popup-close"
              aria-label="Close offer"
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center z-10 active:scale-95 transition-transform"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      )}

      {/* WhatsApp Floating Button */}
      <a
        href={waLink('Hi, I am interested in Bitz Club membership')}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="w-7 h-7"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* View Stories floating pill (bottom-right, above WhatsApp) */}
      <button
        onClick={() => setStoriesSignal(s => s + 1)}
        className="fixed bottom-24 right-6 z-50 group flex items-center gap-2 pl-2 pr-4 py-2 bg-black/70 backdrop-blur-md border border-[#D4AF37]/40 rounded-full text-white text-xs sm:text-sm font-medium hover:bg-black/90 transition-all hover:scale-105 shadow-lg shadow-black/30"
        data-testid="view-stories-btn"
        aria-label="View Stories"
      >
        <span className="relative flex w-8 h-8 rounded-full bg-gradient-to-tr from-[#D4AF37] via-[#F59E0B] to-[#D4AF37] p-[2px]">
          <span className="flex w-full h-full rounded-full bg-black items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
          </span>
        </span>
        <span className="tracking-wide">Stories</span>
      </button>
    </div>
  );
};

export default HomePage;
